import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Typography from '@material-ui/core/Typography';
import FreeCsvDownloadDialog from '../csv-dialog/FreeCsvDownloadDialog';
import moment from 'moment-timezone';
import { getOrganizationsByIds } from '../../../utils/bmResidenceService';
import Redux from '../../../redux/ReduxConnector';
import { CsvDownloadType, registerDownloadLog } from '../../../utils/csvDownloadLogService';
import { setAdditonalService, SumamoruAdditionalServices } from '../../../enums/common/sumamoru-additional-services';
import { SumamoruPlan } from '../../../enums/common/sumamoru-plan-types';
import { getApplicationsData } from '../../../services/dynamodb-service';
import * as csvHelper from '../../../utils/csv-helper';
import { DATE_FORMAT_SLASH, formatDate } from '../../../utils/date-utils';
import { sumamoruProjectTypes } from '../../../config/baseConfig';
import { SearchFilters } from '../../search-api/all-space-index';
import {
  SumamoruContractItem,
  SumamoruAddOnData
} from '../../search-api/search-api-client';
import SearchApiIndex from '../../search-api/all-space-index';

interface P {
  index: SearchApiIndex;
  onClose: () => void;
  onComplete: () => void;
}

interface CsvRow {
  contractId: string;
  companyName: string;
  sumamoruCode: string;
  postcode: string;
  address: string;
  buildingName: string;
  propertyName: string;
  status: string;
  deviceCount: number;
  occupyScheduledDate: string;
  sumamoruApplicationStatus: string;
  sumamoruElectricalStatus: string;
  sumamoruServiceStatus: string;
  customerLastName: string;
  customerFirstName: string;
  customerLastNameKana: string;
  customerFirstNameKana: string;
  customerPhoneNumber: string;
  customerEmail: string;
  customerBirthDay: string;
  hasElectricContract: string;
  useGasPlanFlag: string;
  paymentMethodSelection: string;
  electricityBasePlanSelection: string;
  useGasPlanText: string;
  electricityStartDate: string;
  gasBasePlanSelection: string;
  gasBasePlanSelectionText: string;
  gasStartDate: string;
  gasStartTimeSelection: string;
  witnessGasStart: string;
  witnessNameGasStart: string;
  phoneNumberForGasStart: string;
  serviceStopDate: string;
  isModifiedInSameDay: string;
  isCanceledInSameDay: string;
  linkageId: string;
  createdAt: string;
  applicationUrlPublishedDate: string;
  contractStartDate: string;
  isOverServiceStopDate: string;
  installedDevices: string;
  sumamoruAdditionalService1: string;
  sumamoruAdditionalService2: string;
}

/**
 * スマモル賃貸サービスの種類です。
 *
 * OG域内: 大阪ガス様の管理内のうち関西圏内に適用可能なプラン
 * OG域内 電気のみ: 大阪ガス様の管理内のうち関西圏外に適用可能なプランで、電気のみのプラン
 * OG域外: 大阪ガス様の管理内のうち関西圏外(中部九州)に適用可能なプラン
 * CDE: CDE様の管理内に適用可能なプラン
 * CDE 電気のみ: CDE様の管理内に適用可能なプランで、電気のみのプラン
 * スマモルでない通常物件: スマモル組織においてスマモル賃貸サービスを適用しないプラン
 *
 * 以下は2024/04時点で運用されていないプラン
 * 京葉ガス: 京葉ガス様の管理内に適用可能なプラン
 * 西部ガス: 西部ガス様の管理内に適用可能なプラン
 *
 */

const FilterSelectedArea = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 8px;
  box-sizing: border-box;
`;

export enum SumamoruApplicationStatus {
  application_request = 'application_request', // 申請依頼
  application_request_canceled = 'application_request_canceled', // 申請依頼キャンセル済み
  applied = 'applied', // 申請受付(申込み済)
  applied_canceled = 'applied_canceled' // 申請受付後の取消
}

export enum SumamoruServiceStatus {
  all_in_use = 'all_in_use', // 全サービス利用中
  only_electrical_canceled = 'only_electrical_canceled', // 電気サービスのみ解約
  all_canceled = 'all_canceled' // 全サービス解約済
}

export enum SpaceStatus {
  vacant = 'vacant', // 空室(公開中)
  applied = 'applied', // 申し込み有
  occupyScheduled = 'occupyScheduled', // 入居予定
  occupied = 'occupied', // 入居中
  leaveScheduled = 'leaveScheduled', // 退去予定
  leaveProcessing = 'leaveProcessing', // 退去処理中
  closed = 'closed', // 停止中
  none = 'none'
}

// SpaceStatusとDictKeyのマッピング定義
// Enum値が追加された際に、本ファイルの修正漏れを型チェックを利用して防ぐために切り出し
const SpaceStatusDictValueDefinition: { [key in SpaceStatus]: string } = {
  [SpaceStatus.vacant]: '公開中',
  [SpaceStatus.applied]: '申込有',
  [SpaceStatus.occupyScheduled]: '入居予定',
  [SpaceStatus.occupied]: '入居中',
  [SpaceStatus.leaveScheduled]: '退去予定',
  [SpaceStatus.leaveProcessing]: '退去処理中',
  [SpaceStatus.closed]: '停止中',
  [SpaceStatus.none]: 'ステータスなし'
};

const SumamoruApplicationStatusDictValueDefinition: {
  [key in SumamoruApplicationStatus]: string;
} = {
  [SumamoruApplicationStatus.application_request]: 'URL発行済',
  [SumamoruApplicationStatus.application_request_canceled]: 'URL発行後取消',
  [SumamoruApplicationStatus.applied]: '申込完了',
  [SumamoruApplicationStatus.applied_canceled]: '申込完了後取消'
};

const SumamoruServiceStatusDictValueDefinition: {
  [key in SumamoruServiceStatus]: string;
} = {
  [SumamoruServiceStatus.all_in_use]: '全サービス利用中',
  [SumamoruServiceStatus.only_electrical_canceled]: '電気のみ解約',
  [SumamoruServiceStatus.all_canceled]: '全サービス解約済'
};

const NO_RESULT_CODE = '---';

const PropertiesCsvDownloadDialog: React.FC<P> = props => {
  const { onClose, onComplete, index } = props;

  const onDownloadComplete = useCallback(async () => {
    // ログ記録API
    const res = await registerDownloadLog(CsvDownloadType.ALL_PROPERTY);
    if (!res) console.log('error: 全件ダウンロード: ログ記録失敗しました。');
    onComplete();
  }, [onComplete]);
  const [nbHits, setNbHits] = useState<number>(0);
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);

  const csvHeaders = useMemo(
    () => [
      {
        key: 'contractId',
        label: '契約ID'
      },
      {
        key: 'companyName',
        label: '管理会社名'
      },
      {
        key: 'sumamoruCode',
        label: '建物No'
      },
      {
        key: 'postcode',
        label: '郵便番号'
      },
      {
        key: 'address',
        label: '住所'
      },
      {
        key: 'buildingName',
        label: '建物名'
      },
      {
        key: 'propertyName',
        label: '物件名'
      },
      {
        key: 'status',
        label: '物件ステータス'
      },
      {
        key: 'deviceCount',
        label: 'デバイス設置数'
      },
      {
        key: 'occupyScheduledDate',
        label: '入居予定日'
      },
      {
        key: 'sumamoruApplicationStatus',
        label: 'スマモル申込状況'
      },
      {
        key: 'sumamoruElectricalStatus',
        label: 'スマモル申込内容'
      },
      {
        key: 'sumamoruServiceStatus',
        label: 'スマモル利用状況'
      },
      {
        key: 'customerLastName',
        label: '氏名（姓）'
      },
      {
        key: 'customerFirstName',
        label: '氏名（名）'
      },
      {
        key: 'customerLastNameKana',
        label: 'フリガナ（姓）'
      },
      {
        key: 'customerFirstNameKana',
        label: 'フリガナ（名）'
      },
      {
        key: 'customerPhoneNumber',
        label: '電話番号'
      },
      {
        key: 'customerEmail',
        label: 'メールアドレス'
      },
      {
        key: 'customerBirthDay',
        label: '生年月日'
      },
      {
        key: 'hasElectricContract',
        label: '電気利用有無'
      },
      {
        key: 'useGasPlanFlag',
        label: 'ガス利用有無'
      },
      {
        key: 'paymentMethodSelection',
        label: 'お支払い方法'
      },
      {
        key: 'electricityBasePlanSelection',
        label: '電気申し込みプラン'
      },
      {
        key: 'useGasPlanText',
        label: '電気オプション割引'
      },
      {
        key: 'electricityStartDate',
        label: '電気の使用開始日'
      },
      {
        key: 'gasBasePlanSelection',
        label: 'ガスのお申し込みプラン'
      },
      {
        key: 'gasBasePlanSelectionText',
        label: 'ガスのオプション割引'
      },
      {
        key: 'gasStartDate',
        label: 'ガスの使用開始日'
      },
      {
        key: 'gasStartTimeSelection',
        label: 'ガスの使用開始日(時間帯)'
      },
      {
        key: 'witnessGasStart',
        label: '開栓の立ち合い者'
      },
      {
        key: 'witnessNameGasStart',
        label: '立ち合い者の氏名'
      },
      {
        key: 'phoneNumberForGasStart',
        label: '当日の連絡先'
      },
      {
        key: 'serviceStopDate',
        label: '退去日'
      },
      {
        key: 'isModifiedInSameDay',
        label: '変更フラグ'
      },
      {
        key: 'isCanceledInSameDay',
        label: 'キャンセルフラグ'
      },
      {
        key: 'linkageId',
        label: '紐付ID'
      },
      {
        key: 'createdAt',
        label: 'TaKuTy登録完了日'
      },
      {
        key: 'applicationUrlPublishedDate',
        label: '申込URL送信日'
      },
      {
        key: 'contractStartDate',
        label: '契約開始日'
      },
      {
        key: 'isOverServiceStopDate',
        label: '入居期間'
      },
      {
        key: 'installedDevices',
        label: '利用デバイス'
      },
      {
        key: 'sumamoruAdditionalService1',
        label: '付帯サービスID1'
      },
      {
        key: 'sumamoruAdditionalService2',
        label: '付帯サービスID2'
      }
    ],
    []
  );

  const fileName = useMemo(() => `sumamoryProperties.csv`, []);

  let getDownloadData: () => void;
  getDownloadData = useCallback(async () => {
    const filters: SearchFilters = {
      'sumamoruData.sumamoruLinkable': ['true'],
      attributes: ['property'],
      'sumamoruData.sumamoruProjectTypes': sumamoruProjectTypes
    };
    const excludeFilters: SearchFilters = {};

    const allHits = await index.getAllHitsByBrowseResponse({
      filters: filters,
      excludeFilters: excludeFilters,
      offset: 0
    });

    const resultsList = allHits.map(hit =>
      hit.sumamoruData && hit.sumamoruData.contracts && hit.sumamoruData.contracts.length > 0
      ? hit.sumamoruData.contracts.map(contract => ({
        ...hit,
        contract
      }))
      : [hit]
    );
    const hits = resultsList.flat(); // 1物件に複数契約つく場合があり、画面に表示するのは1契約毎
    const uniqOrganizationIds: Array<string> = Array.from(new Set(hits.map(v => v.organizationId))).filter(v => !!v);

    const propertyIds: Array<string> = Array.from(new Set(hits.map(v => v.id))).filter(v => !!v);

    const uniqOrganizationsRes = await getOrganizationsByIds(uniqOrganizationIds, Redux.getStore().user.accessToken);
    const uniqOrganizations = uniqOrganizationsRes.data ? uniqOrganizationsRes.data : [];

    const applicationsRes = await getApplicationsData(propertyIds);

    const convertedRows = hits.map(res => {
      const companyName = (
        uniqOrganizations.find(v => v.id === res.organizationId) || {
          name: NO_RESULT_CODE
        }
      ).name;
      const propertySumamoru = (res.addOns && res.addOns.sumamoruData) || ({} as Partial<SumamoruAddOnData>);
      const contractSumamoru: Partial<SumamoruContractItem> =
        (res.contract as Partial<SumamoruContractItem>) || {};

      const contractsInfo =
        applicationsRes.find(v => v.propertyId === res.id && v.contractId === contractSumamoru.contractId) || {};

      // 建物での選択可能プラン (DB的には複数入るが座組的には1つなのでこれだけ出す予定)
      const sumamoruAdditionalServices = propertySumamoru.sumamoruAdditionalServices || [];

      const sumamoruPlan =
        Array.isArray(propertySumamoru.sumamoruPlans) && propertySumamoru.sumamoruPlans.length > 0
          ? propertySumamoru.sumamoruPlans.join('|')
          : '';

      // 付帯サービス
      const [sumamoruAdditionalService1 = '', sumamoruAdditionalService2 = ''] =
        sumamoruAdditionalServices.length > 0
          ? propertySumamoru.sumamoruAdditionalServices || []
          : setAdditonalService(sumamoruPlan as SumamoruPlan);

      // 利用デバイス
      const installedDevices = contractSumamoru.installedDevices || [];
      const installedDeviceModel =
        installedDevices && installedDevices.length > 0
          ? installedDevices.reduce((latest, device) =>
              device.installationDate > latest.installationDate ? device : latest
            ).model
          : NO_RESULT_CODE;

      return {
        companyName,
        sumamoruCode: propertySumamoru.sumamoruCode || NO_RESULT_CODE,
        postcode: res.postcode || NO_RESULT_CODE,
        address: res.address || NO_RESULT_CODE,
        buildingName:
          res.parentNames && res.parentNames[0] ? res.parentNames[res.parentNames.length - 1] : NO_RESULT_CODE,
        propertyName: res.name,
        status: !!res.status && res.status !== 'none' ? SpaceStatusDictValueDefinition[res.status] : NO_RESULT_CODE,
        occupyScheduledDate: contractSumamoru.occupyScheduledDate
          ? moment.tz(contractSumamoru.occupyScheduledDate, 'Asia/Tokyo').format('YYYY/MM/DD')
          : NO_RESULT_CODE,
        sumamoruApplicationStatus: contractSumamoru.applicationStatus
          ? SumamoruApplicationStatusDictValueDefinition[contractSumamoru.applicationStatus]
          : 'URL未発行',
        sumamoruServiceStatus:
          contractSumamoru.sumamoruStatus && contractSumamoru.applicationStatus === SumamoruApplicationStatus.applied
            ? SumamoruServiceStatusDictValueDefinition[contractSumamoru.sumamoruStatus]
            : '未申込',
        deviceCount: Array.isArray(res.bitlockIds) ? res.bitlockIds.length : 0,
        sumamoruElectricalStatus:
          contractSumamoru.hasElectricContract === undefined
            ? NO_RESULT_CODE
            : contractSumamoru.hasElectricContract
            ? '電気あり'
            : 'サービスのみ',
        sumamoruAdditionalService1:
          sumamoruAdditionalService1 +
          csvHelper.getAdditionalServiceSuffix(sumamoruAdditionalService1 as SumamoruAdditionalServices, contractsInfo),
        sumamoruAdditionalService2:
          sumamoruAdditionalService2 +
          csvHelper.getAdditionalServiceSuffix(sumamoruAdditionalService2 as SumamoruAdditionalServices, contractsInfo),
        customerLastName: contractsInfo.customerLastName || NO_RESULT_CODE,
        customerFirstName: contractsInfo.customerFirstName || NO_RESULT_CODE,
        customerLastNameKana: contractsInfo.customerLastNameKana || NO_RESULT_CODE,
        customerFirstNameKana: contractsInfo.customerFirstNameKana || NO_RESULT_CODE,
        customerPhoneNumber: contractsInfo.customerPhoneNumber || NO_RESULT_CODE,
        customerEmail: contractsInfo.customerEmail || NO_RESULT_CODE,
        customerBirthDay: contractsInfo.customerBirthDay || NO_RESULT_CODE,
        hasElectricContract:
          (contractsInfo.hasElectricContract !== undefined
            ? csvHelper.getElectricContractText[
                String(contractsInfo.hasElectricContract) as keyof typeof csvHelper.getElectricContractText
              ]
            : undefined) || NO_RESULT_CODE,
        useGasPlanFlag:
          (contractsInfo.useGasPlanSelection !== undefined
            ? csvHelper.getUseGasPlanSelectionText[contractsInfo.useGasPlanSelection]
            : undefined) || NO_RESULT_CODE,
        paymentMethodSelection:
          (contractsInfo.paymentMethodSelection !== undefined
            ? csvHelper.getPaymentMethodSelectionText[contractsInfo.paymentMethodSelection]
            : undefined) || NO_RESULT_CODE,
        electricityBasePlanSelection:
          (contractsInfo.electricityBasePlanSelection !== undefined
            ? csvHelper.getElectricityBasePlanSelectionText[contractsInfo.electricityBasePlanSelection]
            : undefined) || NO_RESULT_CODE,
        useGasPlanText: contractsInfo.useGasPlanSelection === '0' ? 'ガスセット割引' : NO_RESULT_CODE,
        electricityStartDate: formatDate(contractsInfo.electricityStartDate, DATE_FORMAT_SLASH) || NO_RESULT_CODE,
        gasBasePlanSelection:
          (contractsInfo.gasBasePlanSelection !== undefined
            ? csvHelper.getGasBasePlanSelectionText[contractsInfo.gasBasePlanSelection]
            : undefined) || NO_RESULT_CODE,
        gasBasePlanSelectionText: contractsInfo.gasBasePlanSelection === '0' ? '電気セット割引' : NO_RESULT_CODE,
        gasStartDate: formatDate(contractsInfo.gasStartDate, DATE_FORMAT_SLASH) || NO_RESULT_CODE,
        gasStartTimeSelection:
          (contractsInfo.gasStartTimeSelection !== undefined
            ? csvHelper.getGasStartTimeSelectionText[contractsInfo.gasStartTimeSelection]
            : undefined) || NO_RESULT_CODE,
        witnessGasStart:
          (contractsInfo.witnessGasStart !== undefined
            ? csvHelper.getWitnessGasStartText[contractsInfo.witnessGasStart]
            : undefined) || NO_RESULT_CODE,
        witnessNameGasStart: contractsInfo.witnessNameGasStart || NO_RESULT_CODE,
        phoneNumberForGasStart: contractsInfo.phoneNumberForGasStart || NO_RESULT_CODE,
        serviceStopDate: formatDate(contractsInfo.serviceStopDate, DATE_FORMAT_SLASH) || NO_RESULT_CODE,
        isModifiedInSameDay: contractsInfo.isModifiedInSameDay === true ? '更新' : NO_RESULT_CODE,
        isCanceledInSameDay: contractsInfo.isCanceledInSameDay === true ? 'キャンセル' : NO_RESULT_CODE,
        linkageId: contractsInfo.linkageId || NO_RESULT_CODE,
        createdAt: formatDate(contractsInfo.createdAt, DATE_FORMAT_SLASH) || NO_RESULT_CODE,
        contractStartDate: formatDate(contractSumamoru.contractStartDate, DATE_FORMAT_SLASH) || NO_RESULT_CODE,
        isOverServiceStopDate:
          contractsInfo.isOverServiceStopDate === null || contractsInfo.isOverServiceStopDate === undefined
            ? NO_RESULT_CODE
            : contractsInfo.isOverServiceStopDate
            ? '2年以上'
            : '2年以内',
        applicationUrlPublishedDate:
          formatDate(contractSumamoru.applicationUrlPublishedDate, DATE_FORMAT_SLASH) || NO_RESULT_CODE,
        installedDevices: installedDeviceModel,
        contractId: contractSumamoru.contractId || NO_RESULT_CODE
      };
    });
    setCsvRows(convertedRows);
    setNbHits(hits.length);
    return;
  }, [index]);

  const confirmButton = useMemo(
    () => ({
      text: 'ダウンロード',
      onClick: onDownloadComplete,
      disabled: !csvRows || (csvRows && csvRows.length <= 0)
    }),
    [csvRows, onDownloadComplete]
  );

  const cancelButton = useMemo(
    () => ({
      text: 'キャンセル',
      onClick: onClose,
      disabled: false
    }),
    [onClose]
  );

  useEffect(() => {
    getDownloadData();
  }, [getDownloadData]);

  return (
    <FreeCsvDownloadDialog
      title={'スマモル物件全件ダウンロード'}
      confirmButton={confirmButton}
      cancelButton={cancelButton}
      processing={false}
      titleColor={true}
      csvHeaders={csvHeaders}
      csvRows={csvRows}
      fileName={fileName || '無題'}
      onClickDownload={onDownloadComplete}
    >
      <Typography
        style={{
          boxSizing: 'border-box',
          padding: 8,
          borderBottom: '1px solid #E0E0E0'
        }}
      >
        件数 : {nbHits}件
      </Typography>
      <FilterSelectedArea></FilterSelectedArea>
    </FreeCsvDownloadDialog>
  );
};

export default PropertiesCsvDownloadDialog;
